import React from "react";
import "./Chart.scss";

export type DataPoint = {
  x: number;
  y: number;
};

type ChartProps = {
  data: DataPoint[];
  chartType: "line" | "bubble";
  description?: string;
  width?: number;
  height?: number;
};

export const Chart = ({
  data,
  chartType,
  description = "",
  width = 320,
  height = 400,
}: ChartProps) => {
  const padding = 60;
  const chartWidth = width - padding * 1.75;
  const chartHeight = height - padding * 2;

  // Sort data by x value to ensure proper line drawing
  const sortedData = [...data].sort((a, b) => a.x - b.x);

  // Calculate min/max values for proper scaling
  const xMin = 2; // Fixed minimum for both charts to ensure consistent axes
  const xMax = 12; // Fixed maximum for both charts to ensure consistent axes
  const yMin = 0; // Starting from 0 for consistency
  const yMax = 1; // Normalizing to 1 for proper scale display

  // Scale the data points to fit the chart dimensions
  const xScale = (x: number) => {
    return ((x - xMin) / (xMax - xMin)) * chartWidth + padding;
  };

  const yScale = (y: number) => {
    return padding + (1 - (y - yMin) / (yMax - yMin)) * chartHeight;
  };

  // Generate the SVG path for the line chart
  const generateLinePath = () => {
    if (sortedData.length < 2) return ""; // No path if there are fewer than 2 points

    if (sortedData.length === 2) {
      // Draw a straight line if there are exactly 2 points
      const [point1, point2] = sortedData;
      const x1 = xScale(point1.x);
      const y1 = yScale(point1.y);
      const x2 = xScale(point2.x);
      const y2 = yScale(point2.y);

      return `M ${x1} ${y1} L ${x2} ${y2}`; // Straight line path
    }

    // Use Bézier curve for more than 2 points
    return sortedData
      .map((point, i) => {
        const x = xScale(point.x);
        const y = yScale(point.y);

        if (i === 0) {
          // Start the path
          return `M ${x} ${y}`;
        }

        const prevPoint = sortedData[i - 1];
        const prevX = xScale(prevPoint.x);
        const prevY = yScale(prevPoint.y);

        // Calculate control points for the Bézier curve
        const controlX1 = (prevX + x) / 2;
        const controlY1 = prevY;
        const controlX2 = (prevX + x) / 2;
        const controlY2 = y;

        // Create a smooth curve using cubic Bézier
        return `C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${x} ${y}`;
      })
      .join(" ");
  };

  // Generate x-axis ticks - evenly spaced from 2 to 12
  const xTicks = Array.from({ length: 11 }, (_, i) => {
    const value = i + 2; // Starting from 2 and going to 12
    return {
      value,
      position: xScale(value),
    };
  });

  // Generate y-axis values - positioned inside the gradient bar
  const yTicks = Array.from({ length: 11 }, (_, i) => {
    const value = (10 - i) / 10; // Values from 1.0 to 0.0
    const percentage = (1 - value) * 95; // Match the percentage logic of yScale

    return {
      value,
      position: percentage,
    };
  });

  return (
    <div className="chart-wrapper">
      {description && <p className="chart-description">{description}</p>}
      <div className="chart-container" style={{ width, height }}>
        {/* Y-axis with gradient, values inside, and arrow */}
        <div className="y-axis">
          <div className="y-axis-gradient">
            <div className="y-axis-arrow"></div>
            <div className="y-axis-text">More products</div>

            {/* Y-axis values inside the gradient bar */}
            {yTicks.map((tick, i) => (
              <div
                key={i}
                className="y-axis-tick"
                style={{ top: `${tick.position}%` }}
              >
                {tick.value.toFixed(1)}
              </div>
            ))}
          </div>
        </div>

        {/* Y-axis label */}
        <div className="y-axis-label">Absorbance</div>

        {/* "Fewer products" text at the bottom of the y-axis */}
        <div className="fewer-products-text">Fewer products</div>

        {/* Main chart area */}
        <svg className="chart-svg">
          {/* X-axis */}
          <line
            x1={padding}
            y1={height - padding}
            x2={width - padding + 25} // Leave space for arrow
            y2={height - padding}
            className="axis"
          />

          {/* X-axis arrow */}
          <polyline
            points={`${width - padding + 15},${height - padding - 7} ${width - padding + 25},${height - padding} ${width - padding + 15},${height - padding + 7}`}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="x-axis-arrow"
          />

          {/* X-axis ticks */}
          {xTicks.map((tick, i) => (
            <g key={i}>
              <text
                x={tick.position}
                y={height - padding + 20}
                className="axis-text"
                textAnchor="middle"
              >
                {tick.value}
              </text>
            </g>
          ))}

          <text
            x={width / 2}
            y={height - 10}
            className="axis-label"
            textAnchor="middle"
          >
            pH
          </text>

          {/* Chart content */}
          {chartType === "line" ? (
            <path
              d={generateLinePath()}
              className="line-path"
              style={{ strokeWidth: 2 }} // Narrow the line
            />
          ) : (
            <>
              {/* Lines connecting bubbles - now solid instead of dashed */}
              {sortedData.map((point, index) => {
                if (index < sortedData.length - 1) {
                  const nextPoint = sortedData[index + 1];
                  return (
                    <line
                      key={`connector-${index}`}
                      x1={xScale(point.x)}
                      y1={yScale(point.y)}
                      x2={xScale(nextPoint.x)}
                      y2={yScale(nextPoint.y)}
                      className="bubble-connector"
                    />
                  );
                }
                return null;
              })}

              {/* Bubbles */}
              {sortedData.map((point, index) => (
                <g key={index}>
                  <circle
                    cx={xScale(point.x)}
                    cy={yScale(point.y)}
                    r="12"
                    className="bubble"
                  />
                  <text
                    x={xScale(point.x)}
                    y={yScale(point.y)}
                    className="bubble-text"
                    textAnchor="middle"
                    dy=".3em"
                    style={{ fontSize: "8px" }}
                  >
                    {point.y.toFixed(3)}
                  </text>
                </g>
              ))}
            </>
          )}
        </svg>
      </div>
    </div>
  );
};
